import type { components } from '@/api/schema';
import { JOB_POLL_SLOW_INTERVAL_MS } from '@/hooks/api/useReadings';

type JobStatus = components['schemas']['JobStatusResponse'];

/** How long each loading caption stays up before the next one replaces it. */
export const LOADING_STAGE_INTERVAL_MS = 10_000;
// Used until the first status response arrives, or if the backend sent no estimate.
export const DEFAULT_READING_ESTIMATE_MS = 60_000;
// Added to the backend's average: finishing a bit early beats running late,
// and it covers the gap until the next status poll sees the finished job.
export const ESTIMATE_PADDING_MS = 5_000;
// A job that finished right at the estimate is only seen on the next status
// poll (up to the slowest poll interval later), so "taking longer than usual"
// captions wait that long instead of showing up for a normal reading.
export const OVERTIME_GRACE_MS = JOB_POLL_SLOW_INTERVAL_MS;

// A queued job hasn't started, so the bar shouldn't run ahead of the work.
const PENDING_PROGRESS_CAP = 0.1;
// The bar reaches this share when the average duration is up, then creeps
// toward the ceiling so it keeps moving on slow readings without ever lying
// that it's done.
const ON_ESTIMATE_PROGRESS = 0.9;
const RUNNING_PROGRESS_CEILING = 0.98;
const OVERTIME_EASING_MS = 30_000;

/**
 * Client-clock timestamp of the job's creation. Anchors on the server-measured
 * `elapsedMs` at the moment the response was received (`receivedAt`), so a
 * device with a wrong clock still gets a correct elapsed time; falls back to
 * `createdAt` for responses without it.
 */
export function getJobStartedAt(data: JobStatus, receivedAt: number): number {
  if ('elapsedMs' in data && typeof data.elapsedMs === 'number') {
    return receivedAt - data.elapsedMs;
  }
  return Date.parse(data.createdAt);
}

export function getJobEstimateMs(data: JobStatus | undefined): number {
  if (data && 'estimatedDurationMs' in data && data.estimatedDurationMs) {
    return data.estimatedDurationMs + ESTIMATE_PADDING_MS;
  }
  return DEFAULT_READING_ESTIMATE_MS;
}

/** Progress share (0–1) for the loading bar. */
export function getReadingProgress(
  status: JobStatus['status'] | undefined,
  elapsedMs: number,
  estimatedMs: number,
): number {
  if (status === 'completed' || status === 'failed') return 1;

  const elapsed = Math.max(0, elapsedMs);
  let progress: number;
  if (elapsed <= estimatedMs) {
    progress = (elapsed / estimatedMs) * ON_ESTIMATE_PROGRESS;
  } else {
    const overtime = elapsed - estimatedMs;
    progress =
      ON_ESTIMATE_PROGRESS +
      (RUNNING_PROGRESS_CEILING - ON_ESTIMATE_PROGRESS) *
        (1 - Math.exp(-overtime / OVERTIME_EASING_MS));
  }

  return status === 'pending'
    ? Math.min(progress, PENDING_PROGRESS_CAP)
    : progress;
}

/**
 * Time left until the estimate, or `null` once the reading has run past it —
 * at that point a number would be a guess, so the caption explains instead.
 */
export function getRemainingMs(
  elapsedMs: number,
  estimatedMs: number,
): number | null {
  const remaining = estimatedMs - Math.max(0, elapsedMs);
  return remaining > 0 ? remaining : null;
}

/**
 * Caption for the loading screen. `stages` rotate every
 * `LOADING_STAGE_INTERVAL_MS`, except the last one, which is held for the final
 * stretch before the estimate and through `OVERTIME_GRACE_MS` after it. Then
 * `overtimeStages` take over.
 */
export function pickLoadingStage(
  elapsedMs: number,
  estimatedMs: number,
  stages: readonly string[],
  overtimeStages: readonly string[],
): string {
  const elapsed = Math.max(0, elapsedMs);

  const overtimeStartMs = estimatedMs + OVERTIME_GRACE_MS;
  if (elapsed >= overtimeStartMs && overtimeStages.length > 0) {
    const index = Math.floor(
      (elapsed - overtimeStartMs) / LOADING_STAGE_INTERVAL_MS,
    );
    return overtimeStages[Math.min(index, overtimeStages.length - 1)];
  }

  const last = stages.length - 1;
  if (estimatedMs - elapsed <= LOADING_STAGE_INTERVAL_MS) {
    return stages[last];
  }
  const index = Math.floor(elapsed / LOADING_STAGE_INTERVAL_MS);
  return stages[Math.min(index, Math.max(last - 1, 0))];
}
